import React, { useCallback, useEffect, useState } from 'react';
import { useEvents } from '~/core/events/eventsContext';
import { usePersistence } from '~/core/persistence/PersistenceContext';
import { UpdateSearchQuery } from '~/core/useSearchState';
import SRError from '~/utils/errors/srError';
import isInputElement from '~/utils/isInputElement';
import useDebounce from './useDebounce';
import useCheckInputValidity from './useShowSearch/useCheckInputValidity';
import useCloseSERPListeners from './useShowSearch/useCloseSERPListeners';
import useInputContainerZIndex from './useShowSearch/useInputContainerZIndex';
import useOpenSERPListeners from './useShowSearch/useOpenSERPListeners';

export type CloseSERP = () => void;

interface IUseShowSearch {
    /** Native search element identifier */
    searchElementSelector: string;
    resultsCount: number;
    searchQuery: string;
    updateSearchQuery: UpdateSearchQuery;
    innerSERPRef: React.RefObject<HTMLDivElement>;
    loadingFinished: boolean;
    resetState: () => void;
    searchTriggerSelector?: string;
    searchContainerSelector?: string;
    instantSearchEnabled?: boolean;
    isInstantSearch: boolean;
}

/**
 * Hook for handling opening and closing SERP
 *
 * @remarks
 * - Creates event listeners on the native search element, that will open SERP on:
 *      - click
 *      - touchend
 *      - paste
 *      - focus
 *
 * > SERP also opens based on persistence information and when the native search field is focused (used for slower connections)
 *
 * - This hook also provides function for closing SERP and state of SERP visibility
 * - Emits open/close events for analytics
 *
 * @returns callback for closing SERP and state of SERP visibility
 *
 * @throws SRError
 */
const useShowSearch = ({
    updateSearchQuery,
    resultsCount,
    searchQuery,
    searchElementSelector,
    innerSERPRef,
    loadingFinished,
    resetState,
    // searchTriggerSelector is used for selecting an element on the page, which intiates the search. For
    // example a button or a magnifying glass icon.
    searchTriggerSelector,
    // selects the element which contains both the search trigger and the search input
    searchContainerSelector,
    instantSearchEnabled,
    // if instant sesarch is enabled, this tells us if the currently open layer is instant or full serp
    isInstantSearch,
}: IUseShowSearch) => {
    const [isOpen, setIsOpen] = useState(false);
    const debouncedIsOpen = useDebounce(isOpen, 50);
    const persistence = usePersistence();
    const {
        eventEmitter: { emitEvent, emitConfirmableEvent },
        eventFactory: { createCloseEvent, createOpenEvent },
    } = useEvents();

    // used when opening full serp layer, we want to enable scroll in instant search, but not in full serp.
    const hideBodyOverflow = () => {
        document.body.style.setProperty('overflow', 'hidden');
        document.documentElement.style.setProperty('overflow', 'hidden');
    };

    const showBodyOverflow = () => {
        document.body.style.removeProperty('overflow');
        document.documentElement.style.removeProperty('overflow');
    };

    const closeSERP = useCallback(() => {
        (async () => {
            const [aaConfirmation] = await emitConfirmableEvent(
                createCloseEvent({
                    searchQuery,
                    resultsCount,
                    loadingFinished,
                    serpType: isInstantSearch ? 'instant' : 'full',
                }),
                150
            );

            setIsOpen(false);
            showBodyOverflow();

            persistence.clearStorage();
            resetState();

            // when the users types some query into full serp while instant search is closed, we want this to propagate
            // to the native search element, so the same query appears in the native input after closing serp window.
            if (instantSearchEnabled) {
                const nativeSearchElements: Element[] = Array.from(
                    document.querySelectorAll(`${searchElementSelector}`)
                );
                nativeSearchElements.forEach((nativeSearchElement: Element) => {
                    if (isInputElement(nativeSearchElement)) {
                        nativeSearchElement.value = searchQuery;
                    }
                });
            }

            console.log(`Close event delivery`, `AA: ${JSON.stringify(aaConfirmation)}`);
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, resultsCount, loadingFinished]);

    const openSERP = (initialQuery?: string): void => {
        setIsOpen(true);
        if (initialQuery) {
            updateSearchQuery(initialQuery);
        }
    };

    useOpenSERPListeners({
        hideBodyOverflow,
        openSERP,
        persistenceInitialQuery: persistence.getSearchQueryFromURL(),
        searchElementSelector,
        searchTriggerSelector,
        isOpen,
        isInstantSearch,
    });

    useCloseSERPListeners({
        searchTriggerSelector,
        closeSERP,
        isOpen,
        searchElementSelector,
        innerSERPRef,
    });

    useInputContainerZIndex({
        searchContainerSelector,
        isInstantSearch,
        isOpen: debouncedIsOpen,
    });

    useCheckInputValidity({ searchElementSelector, searchTriggerSelector, isInstantSearch });

    useEffect(() => {
        if (debouncedIsOpen) {
            emitEvent(createOpenEvent({ serpType: isInstantSearch ? 'instant' : 'full' }));
        } else {
            return;
        }
    }, [debouncedIsOpen, emitEvent]);

    return {
        hideBodyOverflow,
        isOpen: debouncedIsOpen,
        closeSERP,
    };
};
export default useShowSearch;
